// 关于我们页面内容
import { getPortalContentDetail, getPortalContents } from '@/api/portal'
import { findContentByRouteKey, resolveItemLink } from '@/components/home/utils/homeSectionItemLink'
import {
  buildAboutMenuTree,
  collectAboutSlugs,
  HOME_ABOUT_SLUGS,
  normalizeAboutDetail,
  normalizeAbouts,
  resolveAboutIcon
} from '@/components/modules/about/utils/aboutNormalize'

let aboutFetchSeq = 0
let inflightAboutFetch = null
const detailFetchSeqMap = new Map()

// 按 slug 查找关于条目
function findItemBySlug(items, slug) {
  return findContentByRouteKey(items, slug)
}

// 查找一级菜单分类
function findMenuCategory(menuTree, slug) {
  return menuTree.find((category) => category.slug === slug) || null
}

// 按子项 slug 回溯一级分类（不依赖是否已发布文章）
function findParentCategory(menuTree, slug) {
  return menuTree.find((category) => (
    (category.children || []).some((child) => child.slug === slug)
  )) || null
}

// 组装关于分类与子页导航
function buildCategoryView(menuTree, items, slug) {
  if (!slug) return null

  const categoryMenu = findMenuCategory(menuTree, slug) || findParentCategory(menuTree, slug)
  if (!categoryMenu) return null

  const categoryItem = findItemBySlug(items, categoryMenu.slug)

  return {
    slug: categoryMenu.slug,
    title: categoryItem?.title || categoryMenu.name,
    children: (categoryMenu.children || []).map((child) => {
      const childItem = findItemBySlug(items, child.slug)
      return {
        slug: child.slug,
        path: child.path || childItem?.path || '',
        name: childItem?.title || child.name
      }
    })
  }
}

export default {
  namespaced: true,
  state: {
    items: [],
    menuTree: [],
    loading: false,
    loaded: false,
    locale: '',
    scopedRootKey: '',
    detailMap: {},
    detailLoadingMap: {}
  },
  getters: {
    allItems(state) {
      return state.items
    },
    navItems(state) {
      return state.menuTree.map((category) => {
        const item = findItemBySlug(state.items, category.slug)
        return {
          slug: category.slug,
          path: category.path || '',
          name: item?.title || category.name
        }
      })
    },
    homeSlides(state, getters, rootState) {
      return HOME_ABOUT_SLUGS.map((slug) => {
        const item = findItemBySlug(state.items, slug)
        if (!item) return null
        return {
          slug: item.slug,
          title: item.title,
          text: item.summary || '',
          image: item.image,
          link: item.path || resolveItemLink(item, 'INTRODUCTION', rootState.nav.rawMenus)
        }
      }).filter(Boolean)
    },
    topLevelPages(state) {
      return state.menuTree.map((category, index) => {
        const item = findItemBySlug(state.items, category.slug)
        return {
          slug: category.slug,
          path: category.path || item?.path || '',
          title: item?.title || category.name,
          subheading: '',
          summary: item?.summary || '',
          icon: resolveAboutIcon(category.slug, index),
          cardImageUrl: category.cardImageUrl || '',
          children: (category.children || []).map((child) => {
            const childItem = findItemBySlug(state.items, child.slug)
            return {
              slug: child.slug,
              path: child.path || childItem?.path || '',
              name: childItem?.title || child.name
            }
          })
        }
      })
    },
    getBySlug: (state) => (slug) => findItemBySlug(state.items, slug),
    getDetailById: (state) => (id) => {
      if (!id) return null
      const key = String(id)
      return Object.prototype.hasOwnProperty.call(state.detailMap, key)
        ? state.detailMap[key]
        : undefined
    },
    getCategoryBySlug: (state) => (slug) => buildCategoryView(state.menuTree, state.items, slug),
    isValidSlug: (state) => (routeKey) => {
      if (!routeKey) return false
      if (findItemBySlug(state.items, routeKey)) return true
      return collectAboutSlugs(state.menuTree, state.items).has(routeKey)
    },
    isDetailLoading: (state) => (id) => Boolean(state.detailLoadingMap[String(id)])
  },
  mutations: {
    SET_ITEMS(state, { items, locale, scopedRootKey = '' }) {
      state.items = items || []
      state.loaded = true
      state.locale = locale || ''
      state.scopedRootKey = scopedRootKey || ''
    },
    SET_MENU_TREE(state, menuTree) {
      state.menuTree = menuTree || []
    },
    RESET(state) {
      state.items = []
      state.menuTree = []
      state.loaded = false
      state.locale = ''
      state.scopedRootKey = ''
      state.loading = false
      state.detailMap = {}
      state.detailLoadingMap = {}
    },
    CLEAR_DETAIL_CACHE(state) {
      state.detailMap = {}
      state.detailLoadingMap = {}
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_DETAIL(state, { id, detail }) {
      state.detailMap = {
        ...state.detailMap,
        [String(id)]: detail
      }
    },
    SET_DETAIL_LOADING(state, { id, loading }) {
      state.detailLoadingMap = {
        ...state.detailLoadingMap,
        [String(id)]: loading
      }
    }
  },
  actions: {
    async fetchAbouts({ commit, dispatch, state, rootState }, options) {
      const { force = false } = options ?? {}
      const locale = rootState.locale.current
      if (!locale) return

      const scopedRootKey = options?.rootMenu?.id != null ? String(options.rootMenu.id) : ''
      if (!force && state.loaded && state.locale === locale) {
        if (state.scopedRootKey !== scopedRootKey) {
          const menus = await dispatch('nav/ensureRawMenus', null, { root: true })
          commit('SET_MENU_TREE', buildAboutMenuTree(menus, options?.rootMenu || null))
          commit('SET_ITEMS', { items: state.items, locale, scopedRootKey })
        }
        return
      }

      if (force) {
        aboutFetchSeq += 1
        inflightAboutFetch = null
      }

      if (inflightAboutFetch?.locale === locale && !force) {
        return inflightAboutFetch.promise
      }

      const seq = ++aboutFetchSeq
      // task
      const task = (async () => {
        commit('SET_LOADING', true)
        commit('CLEAR_DETAIL_CACHE')
        try {
          const params = { type: 'INTRODUCTION', locale }
          if (force) params._t = Date.now()
          const [contents, menus] = await Promise.all([
            getPortalContents(params),
            dispatch('nav/ensureRawMenus', null, { root: true })
          ])
          if (seq !== aboutFetchSeq) return
          const rootMenu = options?.rootMenu || null
          commit('SET_MENU_TREE', buildAboutMenuTree(menus, rootMenu))
          commit('SET_ITEMS', {
            items: normalizeAbouts(contents, menus, rootMenu),
            locale,
            scopedRootKey
          })
        } catch (error) {
          if (seq !== aboutFetchSeq) return
          commit('SET_MENU_TREE', [])
          commit('SET_ITEMS', { items: [], locale, scopedRootKey })
          throw error
        } finally {
          if (seq === aboutFetchSeq) {
            commit('SET_LOADING', false)
          }
        }
      })()

      inflightAboutFetch = { locale, promise: task }
      try {
        await task
      } finally {
        if (inflightAboutFetch?.promise === task) {
          inflightAboutFetch = null
        }
      }
    },
    async fetchAboutDetail({ commit, rootState, state }, id) {
      const locale = rootState.locale.current
      if (!locale || !id) return null

      const key = String(id)
      if (Object.prototype.hasOwnProperty.call(state.detailMap, key)) {
        return state.detailMap[key]
      }

      // seq
      const seq = (detailFetchSeqMap.get(key) || 0) + 1
      detailFetchSeqMap.set(key, seq)

      commit('SET_DETAIL_LOADING', { id: key, loading: true })
      try {
        const data = await getPortalContentDetail(id, locale)
        if (detailFetchSeqMap.get(key) !== seq) return null

        const listItem = state.items.find((item) => String(item.id) === key) || null
        const detail = normalizeAboutDetail(data, listItem)
        commit('SET_DETAIL', { id: key, detail })
        return detail
      } catch (error) {
        if (detailFetchSeqMap.get(key) !== seq) return null
        commit('SET_DETAIL', { id: key, detail: null })
        throw error
      } finally {
        if (detailFetchSeqMap.get(key) === seq) {
          commit('SET_DETAIL_LOADING', { id: key, loading: false })
        }
      }
    }
  }
}
